define((require) => {
    "use strict";

    const _        = require("underscore");
    const {Logger} = require("shared/util/Logger");

    const log = new Logger("Preconditions");

    let errorLogger = null;

    /**
     * Preconditions class for validating arguments and state.
     *
     * @class Preconditions
     */
    const Preconditions = () => {};

    function isNotNullOrUndefined(expression) {
        return !_.isNull(expression) && !_.isUndefined(expression);
    }

    function expect(expression, errorMessage, element) {
        if (expression === false) {
            if (element) {
                log.error(errorMessage, element);
            } else {
                log.error(errorMessage);
            }

            if (errorMessage instanceof Error || !errorLogger) {
                throw errorMessage;
            } else if (errorLogger) {
                errorLogger.log(errorMessage); // only log no raised errors (above), since unhandled errors are already logged...
            }
        }

        return expression;
    }

    // Takes any argument and return its type. One of:
    // array, object, string, date, regexp, function, boolean number, null, or undefined
    function getType(element) {
        return Object.prototype.toString.call(element).slice(8, -1).toLowerCase();
    }

    function isType(expectedType, element) {
        expectedType = expectedType.toLowerCase();

        function main(element_) {
            const elementType               = getType(element_);
            const errorMessage              = `Expected ${expectedType}, but got ${elementType}. Checked argument: `;
            const elementTypeIsExpectedType = elementType === expectedType;

            expect(elementTypeIsExpectedType, errorMessage, element_);

            return element_;
        }

        return (arguments.length === 1) ? main : main(element);
    }

    Preconditions.prototype = {

        setErrorLogger(value) {
            errorLogger = value;
        },

        /**
         * Checks that the specified argument is not <tt>null</tt> or <tt>undefined</tt>.
         *
         * @param  {*}       argument       The argument to check.
         * @param  {*|[]}    [errorMessage] Optional error message(s).
         * @return {Boolean} True if the check succeeded, false if it failed.
         */
        checkArgument(argument, errorMessage) {
            errorMessage = errorMessage || "Precondition failed, argument is null or undefined.";

            return expect(isNotNullOrUndefined(argument), errorMessage);
        },

        /**
         * Checks all the arguments passed to this method against <tt>checkArgument</tt>.
         *
         * @return {Boolean} True if the check succeeded for each argument, false otherwise.
         */
        checkArguments(...args) {
            let success = true;
            _.each(args, (argument) => {
                success = this.checkArgument(argument) && success;
            });

            return success;
        },

        /**
         * Checks whether the specified instance is an instance of the specified class/constructor function.
         *
         * @param  {*} instance             The instance to check.
         * @param  {Function} expectedClass The class/constructor function that the specified instance needs
         *                                  to be an instance of.
         * @param  {[type]} errorMessage    Optional error message(s).
         *
         * @return {Boolean} True if the check succeeded, false if it failed.
         */
        instanceOf(instance, expectedClass, errorMessage) {
            errorMessage = errorMessage || `Unexpected instance of ${expectedClass}`;

            return expect(instance instanceof expectedClass, errorMessage);
        },

        /**
         * Ensures the truth of an expression involving the state of the calling
         * instance, but not involving any parameters to the calling method.
         *
         * @param  {Boolean} expression     The argument to check.
         * @param  {*|[]}    [errorMessage] Optional error message(s).
         * @param  {*}       [element]      Optional element to log.
         * @return {Boolean} True if the check succeeded, false if it failed.
         */
        checkState(expression, errorMessage, element) {
            return expect(_.isBoolean(expression) && expression, errorMessage || "Invalid state, expected expression to be true.", element);
        },

        /**
         * Simply calls <tt>checkState(false, errorMessage)</tt>.
         *
         * @param  {*|[]}    [errorMessage] Optional error message(s).
         * @param  {*}       [element]      Optional element to log.
         *
         * @return {Boolean} false.
         */
        fail(errorMessage, element) {
            return this.checkState(false, errorMessage, element);
        },

        /**
         * Ensures <tt>warningMessage</tt> is logged and reported via the frontend error log, if any. <p>
         *
         * This function is included here so Preconditions is sufficient to report non fatal errors as well,
         * instead of requiring the error logger to be injected into for example model instances.
         *
         * @param  {*|[]}    [warningMessage] Warnings message(s).
         * @param  {*}       [element]        Optional element to log.
         *
         * @return {Boolean} false.
         */
        warn(warningMessage, element) {
            if (element) {
                log.warn(warningMessage, element);
            } else {
                log.warn(warningMessage);
            }
            if (errorLogger) {
                errorLogger.log(warningMessage);
            }
            return false;
        },

        isType,
        isString:       isType("string"),
        isArray:        isType("array"),
        isObject:       isType("object"),
        isDate:         isType("date"),
        isRegexp:       isType("regexp"),
        isFunction:     isType("function"),
        isBoolean:      isType("boolean"),
        isNumber:       isType("number"),
        isNull:         isType("null"),
        isUndefined:    isType("undefined"),

        // Special cases

        // Element
        isElement(element) {
            expect(_.isElement(element), `Expected an element, got: ${element}`);
        },

        isElementOrTextNode(element) {
            const isElement = _.isElement(element);
            const isTextNode = element && element.nodeType === 3;

            expect(isElement || isTextNode, "Expected either an element or a text node");
        },

        // jQuery Element
        is$element(element) {
            const is$ = element.jquery; // jQuery version number
            const containsElement = !!element.get(0);

            expect(is$ && containsElement, `Expected a jQuery element, got: ${element}`);
        },

        // Model
        isModel(element) {
            expect(_.isObject(element.attributes), "Expected a model");
        },

        // Collection
        isCollection(element) {
            expect(_.isObject(element._byId), "Expected a collection");
        },

        /**
         * Checks for truthiness of a preset condition (cond).
         *
         * @param {String|Function} cond  A string that can be evaluated to a conditional,
         *                                or a function taking one argument and returning
         *                                a "truthy" or "falsy"
         * @param {*}               value The value checked against the condition
         *
         * The function is curried, meaning these are equivalent:
         *      condition("===2", 3);
         *      condition("===2")(3);
         *
         *  Examples:
         *      condition("<2", 1);                 => true
         *      _.map([4,3,2,1], condition(">1"))   => fails when it reaches 1
         *      condition(".length === 3")("foo")   => true (the length of foo is 3)
         *
         */
        condition(cond, value) {
            // eslint-disable-next-line no-new-func
            const condition = _.isFunction(cond) ? cond : new Function("x", `return x${cond};`);

            function main(_value) {
                expect(!!condition(_value), [`The condition wasn't met (value was: ${_value}).`, cond]);
                return _value;
            }

            return (arguments.length === 1) ? main : main(value);
        },

        /**
         * Will throw an error, if called. Use this precondition in abstract methods.
         */
        abstractMethodError() {
            expect(false, "Abstract method, must be implemented by subclass.");
        }

    };

    return new Preconditions();
});



// WEBPACK FOOTER //
// ./src/shared/util/Preconditions.js